import type { Locale } from "@planerius/contracts"
import type { ReactNode } from "react"
import { useState } from "react"

import { accessStore } from "../../access/access-store"
import { changeMyLocale } from "../../api/profile.api"
import { isApiProblem } from "../../api/problem"
import { LOCALE_NAMES, READY_LOCALES } from "../../i18n/dictionary"
import { useText } from "../../i18n/use-text"
import { Button } from "../../ui"
import styles from "./profile.module.css"

/**
 * Выбор языка интерфейса в профиле (решение владельца 26).
 *
 * ПРЕДЛАГАЮТСЯ ТОЛЬКО ГОТОВЫЕ ЯЗЫКИ. Казахский объявлен в контракте и в базе, но словаря
 * у него нет: выбрав его, человек увидел бы русский интерфейс и решил, что переключатель
 * сломан. Список берётся из самих словарей, поэтому забыть его обновить нельзя.
 *
 * ЯЗЫК ПРИМЕНЯЕТСЯ БЕЗ ПЕРЕЗАГРУЗКИ. Он лежит в профиле, а профиль перечитывается тем же
 * путём, что после смены пароля: сохранили — перечитали — подписи сменились. Просить
 * человека обновить страницу значит признаться, что мы не умеем показать своё же
 * изменение.
 *
 * ЯЗЫК — СВОЙСТВО ЧЕЛОВЕКА, И ЭТО СКАЗАНО СЛОВАМИ. Иначе владелец, переключив язык,
 * будет думать, что перевёл кабинет всему салону, — а сообщения клиентам идут на языке
 * КЛИЕНТА и от этой настройки не зависят вовсе.
 */
export function LanguageBlock(props: { readonly current: Locale }): ReactNode {
  const t = useText()
  const [busy, setBusy] = useState<Locale | null>(null)
  const [note, setNote] = useState<{ readonly kind: "ok" | "bad"; readonly text: string } | null>(
    null,
  )

  const pick = (locale: Locale): void => {
    if (locale === props.current || busy !== null) return
    setBusy(locale)
    setNote(null)
    void (async () => {
      try {
        await changeMyLocale(locale)
        // Профиль перечитывается тихо: подписи сменятся сами, без каркаса загрузки.
        await accessStore.refresh()
        setNote({ kind: "ok", text: t("profile.languageSaved") })
      } catch (error) {
        setNote({
          kind: "bad",
          text: isApiProblem(error) ? (error.message ?? t("profile.languageFailed")) : t("profile.languageFailed"),
        })
      } finally {
        setBusy(null)
      }
    })()
  }

  return (
    <section className={styles.block}>
      <h2 className={styles.blockTitle}>{t("profile.language")}</h2>
      <p className={styles.muted}>{t("profile.languageHint")}</p>

      <div className={styles.languages} role="group" aria-label={t("profile.language")}>
        {READY_LOCALES.map((locale) => (
          <Button
            key={locale}
            size="sm"
            variant={locale === props.current ? "primary" : "secondary"}
            disabled={busy !== null}
            aria-pressed={locale === props.current}
            onClick={() => {
              pick(locale)
            }}
          >
            {LOCALE_NAMES[locale]}
          </Button>
        ))}
      </div>

      {note === null ? null : (
        <p
          className={note.kind === "bad" ? styles.error : styles.muted}
          role={note.kind === "bad" ? "alert" : "status"}
        >
          {note.text}
        </p>
      )}
    </section>
  )
}
